import { useEffect, useState } from "react";
import { RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, slDateTime } from "@/lib/api";
import { inputCls } from "@/pages/Login";

const STATUS = { poslano: ["Poslano", "bg-emerald-100 text-emerald-800"], v_cakanju: ["V čakanju", "bg-amber-100 text-amber-800"],
  napaka: ["Napaka – ponovni poskus", "bg-orange-100 text-orange-800"], neuspesno: ["Neuspešno", "bg-red-100 text-red-800"],
  zaustavljeno: ["Zaustavljeno", "bg-stone-200 text-stone-700"] };

function Toggles({ title, kinds, values, onChange, prefix }) {
  return (
    <div className="card-lux p-6 space-y-3">
      <h3 className="font-display text-2xl">{title}</h3>
      {Object.entries(kinds).map(([k, label]) => (
        <div key={k} className="flex items-center justify-between gap-4 py-1.5 border-b border-[#EADBCE]/60 last:border-0">
          <span className="text-sm">{label}</span>
          <Switch checked={values[k]} onCheckedChange={(v) => onChange({ ...values, [k]: v })} data-testid={`${prefix}-${k}`} />
        </div>
      ))}
    </div>
  );
}

function Logs({ reload }) {
  const [logs, setLogs] = useState([]);
  const load = () => api.get("/admin/email/logs").then((r) => setLogs(r.data));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [reload]);
  const retry = async (id) => { try { await api.post(`/admin/email/logs/${id}/retry`); toast.success("Ponovni poskus izveden."); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="card-lux overflow-x-auto" data-testid="email-logs">
      <div className="p-5 flex items-center justify-between"><h3 className="font-display text-2xl">Dnevnik pošiljanja</h3>
        <button onClick={load} className="btn-line px-3 py-1.5 text-xs flex items-center gap-1" data-testid="email-logs-refresh"><RefreshCw size={12} /> Osveži</button></div>
      <table className="w-full text-sm">
        <thead className="bg-[#F3ECE1] text-left"><tr><th className="p-3">Čas</th><th className="p-3">Prejemnik</th><th className="p-3">Zadeva</th><th className="p-3">Vrsta</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
        <tbody>
          {logs.length === 0 && <tr><td colSpan={6} className="p-4 text-[#6C6560]">Ni še poslanih sporočil.</td></tr>}
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-[#EADBCE]" data-testid={`email-log-${l.id}`}>
              <td className="p-3 whitespace-nowrap text-[#6C6560]">{slDateTime(l.created_at)}</td>
              <td className="p-3">{l.to}</td>
              <td className="p-3 max-w-xs truncate" title={l.error || ""}>{l.subject}{l.error && <span className="block text-xs text-red-700 truncate">{l.error}</span>}</td>
              <td className="p-3 text-xs text-[#6C6560]">{l.category} · {l.kind}</td>
              <td className="p-3"><span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${(STATUS[l.status] || [])[1]}`}>{(STATUS[l.status] || [l.status])[0]}</span> <span className="text-xs text-[#968E87]">{l.attempts}×</span></td>
              <td className="p-3">{l.status !== "poslano" && <button onClick={() => retry(l.id)} className="text-xs underline" data-testid="email-log-retry">Poskusi znova</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function EmailSettings() {
  const [s, setS] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => { api.get("/admin/email/settings").then((r) => setS(r.data)); }, [tick]);
  if (!s) return null;
  const save = async () => {
    try { const r = await api.put("/admin/email/settings", { admin_email: s.admin_email, reply_to: s.reply_to, notify: s.notify, customer: s.customer }); setS(r.data); toast.success("E-poštne nastavitve so shranjene."); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    try { await api.post("/admin/email/test"); toast.success(`Testno sporočilo je v pošiljanju na ${s.admin_email}.`); setTimeout(() => setTick((x) => x + 1), 3000); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="email-settings">
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-lux p-6 space-y-4">
          <h3 className="font-display text-2xl">E-poštne nastavitve</h3>
          <label className="block text-sm text-[#6C6560]">Naslov za obvestila administratorju
            <input className={`${inputCls} mt-1`} type="email" value={s.admin_email} onChange={(e) => setS({ ...s, admin_email: e.target.value })} data-testid="email-admin-input" /></label>
          <label className="block text-sm text-[#6C6560]">Naslov za odgovore strank (Reply-To)
            <input className={`${inputCls} mt-1`} type="email" value={s.reply_to} onChange={(e) => setS({ ...s, reply_to: e.target.value })} data-testid="email-replyto-input" /></label>
          <div className="flex gap-2 flex-wrap">
            <button onClick={save} className="btn-dark px-6 h-11 text-sm" data-testid="email-settings-save">Shrani nastavitve</button>
            <button onClick={test} className="btn-line px-6 h-11 text-sm flex items-center gap-2" data-testid="email-test-button"><Send size={14} /> Pošlji testno sporočilo</button>
          </div>
        </div>
        <div className="card-lux p-6 space-y-2 text-sm" data-testid="email-status">
          <h3 className="font-display text-2xl mb-2">Stanje pošiljanja</h3>
          <p>Ponudnik: <b>{s.label}</b></p>
          <p>Ime pošiljatelja: <b>{s.from_name}</b></p>
          <p>Naslov pošiljatelja: <b>{s.from_address || "upravlja platforma Emergent (preverjena domena)"}</b></p>
          <p>Odgovori strank gredo na: <b>{s.reply_to}</b></p>
          <p className="pt-2">Poslano: <b>{s.stats.poslano}</b> · V čakanju: <b>{s.stats.v_cakanju}</b> · Napake: <b>{s.stats.napaka + s.stats.neuspesno}</b> · Zaustavljeno: <b>{s.stats.zaustavljeno}</b></p>
          <p className="text-[#6C6560]">Zadnje uspešno: {s.last_sent ? `${slDateTime(s.last_sent.sent_at)} → ${s.last_sent.to}` : "še ni bilo"}</p>
          {s.provider === "emergent" && (
            <p className="text-xs text-[#6C6560] pt-2 leading-relaxed" data-testid="email-domain-note">Sporočila se zdaj pošiljajo prek preverjene domene platforme z imenom »{s.from_name}«; odgovori prispejo na {s.reply_to}. Za pošiljanje neposredno z naslova {s.business_email} (SPF, DKIM, DMARC na domeni najintrenutek.eu) dodajte v okolje strežnika podatke SMTP vašega ponudnika (SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD, SMTP_FROM) in nato pošljite testno sporočilo.</p>
          )}
          <p className="text-xs text-[#6C6560]">Ne domnevajte, da pošiljanje deluje, dokler testno sporočilo ne prispe v nabiralnik.</p>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <Toggles title="Obvestila administratorju" kinds={s.admin_kinds} values={s.notify} onChange={(v) => setS({ ...s, notify: v })} prefix="email-notify" />
        <Toggles title="Sporočila strankam" kinds={s.customer_kinds} values={s.customer} onChange={(v) => setS({ ...s, customer: v })} prefix="email-customer" />
      </div>
      <Logs reload={tick} />
    </div>
  );
}
